// src/pages/admin/AdminUserDetailPage.tsx
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAdminUser, useUserActivity } from '../hooks';
import { UserDetailPanel } from '../components';

export const AdminUserDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: user, isLoading: userLoading } = useAdminUser(id!);
  const { data: activity, isLoading: activityLoading } = useUserActivity(id!);

  if (userLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 rounded w-48" />
        <div className="h-64 bg-gray-200 rounded" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-12">
        <p className="text-warm-gray">User not found</p>
        <Link to="/admin/users" className="text-sage hover:text-sage-dark mt-2 inline-block">
          Back to Users
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/admin/users" className="text-warm-gray hover:text-navy">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <div>
          <h1 className="text-2xl font-display font-semibold text-navy">User Details</h1>
          <p className="text-warm-gray">{user.email}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <UserDetailPanel user={user} />
        </div>
        <div className="space-y-6">
          {/* Activity Log */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-navy mb-4">Recent Activity</h3>
            {activityLoading ? (
              <div className="animate-pulse space-y-2">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-12 bg-gray-100 rounded" />
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {activity?.items.slice(0, 10).map((item) => (
                  <div key={item.id} className="flex items-start gap-3 p-2 rounded-lg hover:bg-cream">
                    <div className="w-8 h-8 rounded-full bg-sage-light/30 flex items-center justify-center text-sage text-xs">
                      {item.action.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-navy truncate">{item.action}</p>
                      <p className="text-xs text-warm-gray">
                        {new Date(item.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUserDetailPage;


